import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { siteApi } from '@/api/site';
import { trackEvent } from '@/api/track';
import { userApi } from '@/api/user';
import { getUrlHost, trackCreateEvent } from '@/lib/create/createTracking';
import { briefSchema } from '@/lib/create/createPageData';
import {
  buildBlueprintGenerationParams,
  buildBlueprintGenerationPrompt,
  buildBriefPromptFromBlueprint,
  CAMPAIGN_BLUEPRINT_TASK_CODE,
  parseGeneratedCampaignBlueprints,
  toCreationCampaignBlueprintsFromSite,
} from '@/lib/create/campaignBlueprints';
import type { ParsedFileContentJson } from '@/lib/fileParsing';
import { parseMarkdownToBriefSections } from '@/lib/projectTransform';
import { getDefaultProjectTypeId } from '@/lib/projectUtils';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import { ensureAiTaskCredits } from '@/lib/aiTaskCredits';
import { isAiCreditsError } from '@/lib/creditsGuard';
import type { BriefSection, CreationCampaignBlueprint } from '@/types/creation';

type SubmitBriefOptions = {
  isTemplatePrompt?: boolean;
  entrySource?: string;
  siteId?: number | null;
};

type GenerateBriefOptions = {
  entrySource?: string;
};

const buildBlueprintMetaJson = ({
  briefInput,
  websiteUrl,
  brandPdfName,
}: {
  briefInput: string;
  websiteUrl: string;
  brandPdfName: string;
}) =>
  JSON.stringify({
    briefInput,
    websiteUrl: websiteUrl || undefined,
    brandPdfName: brandPdfName || undefined,
    createdAt: new Date().toISOString(),
  });

interface CreationState {
  step: 1 | 2 | 3;
  briefInput: string;
  websiteUrl: string;
  brandPdfName: string;
  brandPdfParsedContent: ParsedFileContentJson | null;
  savedDescription: string;
  isLoading: boolean;
  isBlueprintLoading: boolean;
  isModalOpen: boolean;
  briefSections: BriefSection[];
  campaignBlueprints: CreationCampaignBlueprint[];
  selectedCampaignBlueprintId: string | null;
  briefGenerationDurationMs: number | null;
  websiteAnalysis: string;
  isWebsiteAnalysisLoading: boolean;
  briefGenerationFailed: boolean;
  blueprintGenerationFailed: boolean;
  websiteAnalysisFailed: boolean;
  activeRequestId: number;
  projectTypeId?: number;

  setStep: (step: 1 | 2 | 3) => void;
  setBriefInput: (value: string) => void;
  setWebsiteUrl: (value: string) => void;
  setBrandPdfName: (value: string) => void;
  setBrandPdfParsedContent: (value: ParsedFileContentJson | null) => void;
  setSavedDescription: (value: string) => void;
  setIsLoading: (value: boolean) => void;
  setIsModalOpen: (value: boolean) => void;
  setBriefSections: (sections: BriefSection[]) => void;
  setCampaignBlueprints: (blueprints: CreationCampaignBlueprint[]) => void;
  setSelectedCampaignBlueprintId: (id: string | null) => void;
  setProjectTypeId: (id: number | undefined) => void;
  resetCreationFlow: () => void;
  submitBrief: (options?: SubmitBriefOptions) => void;
  generateBriefFromSelectedBlueprint: (options?: GenerateBriefOptions) => void;
}

export const useCreationStore = create<CreationState>()(
  persist(
    (set, get) => ({
      step: 1,
      briefInput: '',
      websiteUrl: '',
      brandPdfName: '',
      brandPdfParsedContent: null,
      savedDescription: '',
      isLoading: false,
      isBlueprintLoading: false,
      isModalOpen: false,
      briefSections: [],
      campaignBlueprints: [],
      selectedCampaignBlueprintId: null,
      briefGenerationDurationMs: null,
      websiteAnalysis: '',
      isWebsiteAnalysisLoading: false,
      briefGenerationFailed: false,
      blueprintGenerationFailed: false,
      websiteAnalysisFailed: false,
      activeRequestId: 0,
      projectTypeId: getDefaultProjectTypeId(),

      setStep: (step) => set({ step }),
      setBriefInput: (briefInput) => set({ briefInput }),
      setWebsiteUrl: (websiteUrl) => set({ websiteUrl }),
      setBrandPdfName: (brandPdfName) => set({ brandPdfName }),
      setBrandPdfParsedContent: (brandPdfParsedContent) => set({ brandPdfParsedContent }),
      setSavedDescription: (savedDescription) => set({ savedDescription }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setIsModalOpen: (isModalOpen) => set({ isModalOpen }),
      setBriefSections: (briefSections) => set({ briefSections }),
      setCampaignBlueprints: (campaignBlueprints) => set({ campaignBlueprints }),
      setSelectedCampaignBlueprintId: (selectedCampaignBlueprintId) =>
        set({ selectedCampaignBlueprintId }),
      setProjectTypeId: (projectTypeId) => set({ projectTypeId }),

      resetCreationFlow: () =>
        set({
          step: 1,
          briefInput: '',
          websiteUrl: '',
          brandPdfName: '',
          brandPdfParsedContent: null,
          savedDescription: '',
          isLoading: false,
          isBlueprintLoading: false,
          isModalOpen: false,
          briefSections: [],
          campaignBlueprints: [],
          selectedCampaignBlueprintId: null,
          briefGenerationDurationMs: null,
          websiteAnalysis: '',
          isWebsiteAnalysisLoading: false,
          briefGenerationFailed: false,
          blueprintGenerationFailed: false,
          websiteAnalysisFailed: false,
          activeRequestId: 0,
        }),

      submitBrief: (options = {}) => {
        const {
          briefInput,
          websiteUrl,
          brandPdfName,
          brandPdfParsedContent,
          setSavedDescription,
          setStep,
        } = get();

        const result = briefSchema.safeParse(briefInput);
        if (!result.success) {
          toast.error(result.error.issues[0].message);
          return;
        }

        const requestId = Date.now();
        const blueprintGenerationStartedAt = Date.now();
        const websiteUrlVal = websiteUrl.trim();
        const siteId =
          Number.isFinite(Number(options.siteId)) && Number(options.siteId) > 0
            ? Number(options.siteId)
            : undefined;

        trackEvent({
          eventCode: 'create_brief_submit',
          eventData: JSON.stringify({
            prompt_length: briefInput.length,
            has_website_url: Boolean(websiteUrlVal),
            has_brand_pdf: Boolean(brandPdfName),
            is_template_prompt: Boolean(options.isTemplatePrompt),
            entry_source: options.entrySource || 'create_page',
            site_id: siteId ?? null,
          }),
        });

        setSavedDescription(briefInput);
        set({
          isLoading: false,
          isBlueprintLoading: true,
          briefSections: [],
          campaignBlueprints: [],
          selectedCampaignBlueprintId: null,
          briefGenerationDurationMs: null,
          websiteAnalysis: '',
          isWebsiteAnalysisLoading: !!websiteUrlVal,
          briefGenerationFailed: false,
          blueprintGenerationFailed: false,
          websiteAnalysisFailed: false,
          activeRequestId: requestId,
        });
        setStep(2);

        if (websiteUrlVal) {
          const websiteAnalysisStartedAt = Date.now();
          userApi
            .aiGenerateStream(
              {
                taskCode: import.meta.env.VITE_AI_WEBSITE_URL_TASK_CODE,
                params: { url: websiteUrlVal },
              },
              undefined,
              { suppressErrorToast: true, showCreditsModal: false },
            )
            .then((websiteResponse) => {
              if (get().activeRequestId !== requestId) return;
              set({
                websiteAnalysis: websiteResponse.content,
                websiteAnalysisFailed: false,
              });
              trackCreateEvent('create_source_url_analysis_result', {
                result: 'success',
                url_host: getUrlHost(websiteUrlVal),
                duration_ms: Date.now() - websiteAnalysisStartedAt,
                analysis_content_length: websiteResponse.content.length,
                error_type: '',
                prompt_length: briefInput.length,
                site_id: siteId ?? null,
                entry_source: options.entrySource || 'create_page',
              });
            })
            .catch((error: unknown) => {
              if (get().activeRequestId !== requestId) return;
              console.error('Failed to analyze website:', error);
              set({
                websiteAnalysis: '',
                websiteAnalysisFailed: true,
              });
              trackCreateEvent('create_source_url_analysis_result', {
                result: 'failure',
                url_host: getUrlHost(websiteUrlVal),
                duration_ms: Date.now() - websiteAnalysisStartedAt,
                analysis_content_length: 0,
                error_type: getTrackingErrorCode(error),
                prompt_length: briefInput.length,
                site_id: siteId ?? null,
                entry_source: options.entrySource || 'create_page',
              });
            })
            .finally(() => {
              if (get().activeRequestId !== requestId) return;
              set({ isWebsiteAnalysisLoading: false });
            });
        }

        const prompt = buildBlueprintGenerationPrompt({
          briefInput,
          websiteUrl: websiteUrlVal,
          brandPdfParsedContent,
        });

        userApi
          .aiGenerateStream(
            {
              taskCode: CAMPAIGN_BLUEPRINT_TASK_CODE,
              message: prompt,
              siteId,
              params: buildBlueprintGenerationParams({
                briefInput,
                websiteUrl: websiteUrlVal,
                brandPdfName,
                brandPdfParsedContent,
              }),
            },
            undefined,
            { suppressErrorToast: true, showCreditsModal: false, preferFinalContent: true },
          )
          .then(async (blueprintResponse) => {
            if (get().activeRequestId !== requestId) return;

            const generatedBlueprints = parseGeneratedCampaignBlueprints(blueprintResponse.content);
            if (generatedBlueprints.length === 0) {
              set({
                campaignBlueprints: [],
                selectedCampaignBlueprintId: null,
                blueprintGenerationFailed: true,
              });
              trackCreateEvent('campaign_blueprint_generation_result', {
                result: 'empty',
                prompt_length: briefInput.length,
                has_website_url: Boolean(websiteUrlVal),
                has_brand_pdf: Boolean(brandPdfName),
                blueprint_count: 0,
                duration_ms: Date.now() - blueprintGenerationStartedAt,
                error_type: 'NO_PARSABLE_BLUEPRINTS',
                site_id: siteId ?? null,
                entry_source: options.entrySource || 'create_page',
              });
              toast.error('We could not generate campaign blueprints for this request.');
              return;
            }

            if (!siteId) {
              set({
                campaignBlueprints: generatedBlueprints,
                selectedCampaignBlueprintId: null,
                blueprintGenerationFailed: false,
              });
              trackCreateEvent('campaign_blueprint_generation_result', {
                result: 'success',
                prompt_length: briefInput.length,
                has_website_url: Boolean(websiteUrlVal),
                has_brand_pdf: Boolean(brandPdfName),
                blueprint_count: generatedBlueprints.length,
                duration_ms: Date.now() - blueprintGenerationStartedAt,
                error_type: '',
                site_id: null,
                entry_source: options.entrySource || 'create_page',
              });
              toast.success(`${generatedBlueprints.length} blueprints ready.`);
              return;
            }

            const metaJson = buildBlueprintMetaJson({
              briefInput,
              websiteUrl: websiteUrlVal,
              brandPdfName,
            });

            const persisted = await Promise.all(
              generatedBlueprints.map(async (blueprint, index) => {
                try {
                  const saved = await siteApi.createCampaignBlueprint({
                    siteId,
                    title: blueprint.title,
                    summary: blueprint.summary,
                    status: blueprint.status,
                    sourceTaskCode: CAMPAIGN_BLUEPRINT_TASK_CODE,
                    schemaVersion: blueprint.schemaVersion || 1,
                    contentJson: blueprint.contentJson,
                    metaJson,
                    sortOrder: blueprint.sortOrder ?? index,
                  });
                  return saved;
                } catch (error) {
                  console.error('Failed to persist generated campaign blueprint:', error);
                  return null;
                }
              }),
            );

            const savedBlueprints = persisted.filter(
              (item): item is NonNullable<(typeof persisted)[number]> => Boolean(item),
            );
            const normalizedBlueprints =
              savedBlueprints.length > 0
                ? toCreationCampaignBlueprintsFromSite(savedBlueprints)
                : generatedBlueprints;

            set({
              campaignBlueprints: normalizedBlueprints,
              selectedCampaignBlueprintId: null,
              blueprintGenerationFailed: false,
            });
            trackCreateEvent('campaign_blueprint_generation_result', {
              result:
                savedBlueprints.length < generatedBlueprints.length ? 'partial_success' : 'success',
              prompt_length: briefInput.length,
              has_website_url: Boolean(websiteUrlVal),
              has_brand_pdf: Boolean(brandPdfName),
              blueprint_count: normalizedBlueprints.length,
              duration_ms: Date.now() - blueprintGenerationStartedAt,
              error_type:
                savedBlueprints.length < generatedBlueprints.length
                  ? 'BLUEPRINT_PERSIST_PARTIAL_FAILURE'
                  : '',
              site_id: siteId,
              entry_source: options.entrySource || 'create_page',
            });

            if (savedBlueprints.length < generatedBlueprints.length) {
              toast.warning(
                'Blueprints are ready, but some of them could not be saved to the site.',
              );
            } else {
              toast.success(`${normalizedBlueprints.length} blueprints ready.`);
            }
          })
          .catch((error: unknown) => {
            if (get().activeRequestId !== requestId) return;
            console.error('Failed to generate campaign blueprints:', error);
            set({
              campaignBlueprints: [],
              selectedCampaignBlueprintId: null,
              blueprintGenerationFailed: true,
            });
            trackCreateEvent('campaign_blueprint_generation_result', {
              result: 'failure',
              prompt_length: briefInput.length,
              has_website_url: Boolean(websiteUrlVal),
              has_brand_pdf: Boolean(brandPdfName),
              blueprint_count: 0,
              duration_ms: Date.now() - blueprintGenerationStartedAt,
              error_type: getTrackingErrorCode(error),
              site_id: siteId ?? null,
              entry_source: options.entrySource || 'create_page',
            });
            toast.error('We could not generate campaign blueprints for this request.');
          })
          .finally(() => {
            if (get().activeRequestId !== requestId) return;
            set({ isBlueprintLoading: false });
          });
      },

      generateBriefFromSelectedBlueprint: (options = {}) => {
        const state = get();
        const selectedBlueprint = state.campaignBlueprints.find(
          (item) => item.localId === state.selectedCampaignBlueprintId,
        );

        if (!selectedBlueprint) {
          toast.error('Select a blueprint to continue.');
          return;
        }

        const taskCode = import.meta.env.VITE_AI_BRIEF_TASK_CODE;
        const prompt = buildBriefPromptFromBlueprint({
          briefInput: state.savedDescription || state.briefInput,
          blueprint: selectedBlueprint,
          websiteUrl: state.websiteUrl,
          websiteAnalysis: state.websiteAnalysis,
          brandPdfParsedContent: state.brandPdfParsedContent,
        });
        const creditsModalOptions = {
          showCreditsModal: true,
          creditsModalCopy:
            "You don't have enough credits to generate this brief. Top up your account to continue.",
          creditsModalProductArea: 'create',
          creditsModalSurface: 'campaign_blueprint_picker',
          creditsModalStage: 'brief_generation',
          creditsModalTrigger: 'generate',
          creditsModalUiType: 'modal' as const,
          creditsModalSource: 'create_blueprint_picker',
          creditsModalOperation: 'brief_generation',
          creditsModalMetadata: {
            blueprint_id: selectedBlueprint.id ?? selectedBlueprint.localId,
            blueprint_title: selectedBlueprint.title,
            entry_source: options.entrySource || 'blueprint_picker',
          },
        };

        void (async () => {
          try {
            await ensureAiTaskCredits(taskCode, creditsModalOptions);
          } catch (error) {
            if (isAiCreditsError(error)) {
              trackCreateEvent('create_brief_submit_blocked', {
                block_reason: 'insufficient_credits',
                prompt_length: state.briefInput.length,
                has_website_url: Boolean(state.websiteUrl.trim()),
                has_brand_pdf: Boolean(state.brandPdfName.trim()),
                blueprint_id: selectedBlueprint.id ?? selectedBlueprint.localId,
                blueprint_title: selectedBlueprint.title,
                entry_source: options.entrySource || 'blueprint_picker',
              });
              return;
            }

            console.warn('Failed to check point balance before generating brief:', error);
          }

          const requestId = state.activeRequestId || Date.now();
          const briefGenerationStartedAt = Date.now();
          let accumulatedBrief = '';

          if (!state.activeRequestId) {
            set({ activeRequestId: requestId });
          }

          set({
            step: 3,
            isLoading: true,
            briefSections: [],
            briefGenerationDurationMs: null,
            briefGenerationFailed: false,
          });

          trackEvent({
            eventCode: 'campaign_blueprint_continue',
            eventData: JSON.stringify({
              blueprint_title: selectedBlueprint.title,
              blueprint_id: selectedBlueprint.id ?? null,
              prompt_length: state.briefInput.length,
              entry_source: options.entrySource || 'blueprint_picker',
            }),
          });

          userApi
            .aiGenerateStream(
              {
                taskCode,
                message: prompt,
                siteId: selectedBlueprint.siteId,
              },
              (chunk) => {
                if (get().activeRequestId !== requestId) return;
                accumulatedBrief += chunk;
                const sections = parseMarkdownToBriefSections(accumulatedBrief);
                set({ briefSections: sections });
              },
              { suppressErrorToast: true, ...creditsModalOptions },
            )
            .then((briefResponse) => {
              if (get().activeRequestId !== requestId) return;
              const finalBrief = (briefResponse.content || accumulatedBrief).trim();
              const finalSections = parseMarkdownToBriefSections(finalBrief);
              const durationMs = Date.now() - briefGenerationStartedAt;

              if (finalSections.length === 0) {
                console.error('Brief generation returned no parsable sections:', finalBrief);
                trackEvent({
                  eventCode: 'brief_generation_result',
                  eventData: JSON.stringify({
                    result: 'failure',
                    prompt_length: prompt.length,
                    section_count: 0,
                    has_website_url: Boolean(state.websiteUrl.trim()),
                    duration_ms: durationMs,
                    error: 'NO_PARSABLE_SECTIONS',
                    blueprint_title: selectedBlueprint.title,
                  }),
                });
                set({
                  briefSections: [],
                  briefGenerationDurationMs: durationMs,
                  briefGenerationFailed: true,
                });
                return;
              }

              set({
                briefSections: finalSections,
                briefGenerationDurationMs: durationMs,
                briefGenerationFailed: false,
              });
              trackEvent({
                eventCode: 'brief_generation_result',
                eventData: JSON.stringify({
                  result: 'success',
                  prompt_length: prompt.length,
                  section_count: finalSections.length,
                  has_website_url: Boolean(state.websiteUrl.trim()),
                  duration_ms: durationMs,
                  error: '',
                  blueprint_title: selectedBlueprint.title,
                }),
              });
              toast.success('Your brief is ready.');
            })
            .catch((error: unknown) => {
              if (get().activeRequestId !== requestId) return;
              const durationMs = Date.now() - briefGenerationStartedAt;

              if (isAiCreditsError(error)) {
                trackCreateEvent('create_brief_submit_blocked', {
                  block_reason: 'insufficient_credits',
                  prompt_length: state.briefInput.length,
                  has_website_url: Boolean(state.websiteUrl.trim()),
                  has_brand_pdf: Boolean(state.brandPdfName.trim()),
                  blueprint_id: selectedBlueprint.id ?? selectedBlueprint.localId,
                  blueprint_title: selectedBlueprint.title,
                  entry_source: options.entrySource || 'blueprint_picker',
                });
                set({
                  step: 2,
                  briefSections: [],
                  briefGenerationDurationMs: null,
                  briefGenerationFailed: false,
                });
                return;
              }

              console.error('Failed to generate AI content:', error);
              trackEvent({
                eventCode: 'brief_generation_result',
                eventData: JSON.stringify({
                  result: 'failure',
                  prompt_length: prompt.length,
                  section_count: 0,
                  has_website_url: Boolean(state.websiteUrl.trim()),
                  duration_ms: durationMs,
                  error: error instanceof Error ? error.message : String(error || 'unknown_error'),
                  blueprint_title: selectedBlueprint.title,
                }),
              });
              set({
                briefSections: [],
                briefGenerationDurationMs: durationMs,
                briefGenerationFailed: true,
              });
            })
            .finally(() => {
              if (get().activeRequestId !== requestId) return;
              set({ isLoading: false });
            });
        })();
      },
    }),
    {
      name: 'vibe-creation-storage',
      partialize: (state) => ({
        briefInput: state.briefInput,
        websiteUrl: state.websiteUrl,
        savedDescription: state.savedDescription,
        projectTypeId: state.projectTypeId,
      }),
    },
  ),
);
